import { useMemo } from 'react';
import { usePlanRepository } from '@hotjar/webapp/hooks/usePlanRepository';
import { useCurrentSite } from '@hotjar/site';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import { useSessionSettings } from '@hotjar/webapp/features/Settings/hooks';
import { CAPTURE_MODE } from '@hotjar/webapp/domains/common/session-settings/dto';

interface SamplingState {
  inDisabledState: boolean;
  inSuccessState: boolean;
  inWarningState: boolean;
  inErrorState: boolean;
  isLoading: boolean;
}

export const useSamplingState = (): SamplingState => {
  const { data: currentSite } = useCurrentSite();
  const { getState } = useTrackingCode();
  const state = getState(currentSite?.lastData);
  const { data: sessionSettings } = useSessionSettings(currentSite?.id);
  const planRepository = usePlanRepository();
  const hasCC =
    currentSite && currentSite?.effectivePlanId
      ? planRepository.byId(currentSite.effectivePlanId).ccDailyAllowance > 0
      : false;

  const inDisabledState = hasCC && sessionSettings?.sessionCaptureMode === CAPTURE_MODE.OFF;
  const inSuccessState = !inDisabledState && state.installed && state.isActive;
  const inWarningState = !inDisabledState && state.installed && !state.isActive;
  const inErrorState = !inDisabledState && !state.installed;

  return useMemo(
    () => ({
      isLoading: !sessionSettings,
      inDisabledState,
      inSuccessState,
      inWarningState,
      inErrorState,
    }),
    [inDisabledState, inSuccessState, inWarningState, inErrorState, sessionSettings],
  );
};
